import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import {
    ChevronLeft, ChevronRight, Trophy, Medal, Dumbbell, Moon,
    Apple, Scale, Coins, Footprints, Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_META = {
    gold: { icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200', label: { de: 'Starke Woche!', en: 'Strong week!' } },
    silver: { icon: Medal, color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', label: { de: 'Solide Woche', en: 'Solid week' } },
    bronze: { icon: Flame, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200', label: { de: 'Kampf-Woche', en: 'Tough week' } },
    none: { icon: Medal, color: 'text-muted-foreground', bg: 'bg-muted', label: { de: 'Keine Daten', en: 'No data' } },
};

function fmtDate(iso, language) {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: '2-digit' });
}

function fmtSigned(v) {
    return `${v > 0 ? '+' : ''}${Math.round(v).toLocaleString('de-CH')}`;
}

function StatTile({ icon: Icon, iconClass, title, value, sub, testId }) {
    return (
        <Card data-testid={testId}>
            <CardContent className="p-3.5">
                <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${iconClass}`} />
                    <span className="text-xs font-medium text-muted-foreground">{title}</span>
                </div>
                <div className="text-lg font-bold leading-tight">{value}</div>
                {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
            </CardContent>
        </Card>
    );
}

export default function WeeklySummary() {
    const { language } = useLanguage();
    const de = language === 'de';
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/weekly/summary`, { params: { offset } })
            .then(res => setData(res.data))
            .catch(e => console.error('weekly summary failed:', e))
            .finally(() => setLoading(false));
    }, [offset]);

    const grade = GRADE_META[data?.grade] || GRADE_META.none;
    const GradeIcon = grade.icon;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="weekly-summary-page">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto space-y-4">
                <div>
                    <h1 className="text-2xl font-bold">{de ? 'Nanouks Wochenrückblick' : "Nanouk's Weekly Review"}</h1>
                    <p className="text-sm text-muted-foreground">
                        {de ? 'Deine Woche im Überblick – ehrlich und mit Herz.' : 'Your week at a glance – honest and with heart.'}
                    </p>
                </div>

                <div className="flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={() => setOffset(o => Math.min(52, o + 1))} data-testid="weekly-prev-btn">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-semibold" data-testid="weekly-range">
                        {data ? `${fmtDate(data.week_start, language)} – ${fmtDate(data.week_end, language)}` : '…'}
                    </span>
                    <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - 1))} data-testid="weekly-next-btn">
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>

                {loading ? (
                    <Card><CardContent className="p-6 text-center text-sm text-muted-foreground animate-pulse">
                        {de ? 'Nanouk wertet deine Woche aus…' : 'Nanouk is analyzing your week…'}
                    </CardContent></Card>
                ) : !data?.has_data ? (
                    <Card data-testid="weekly-empty-state"><CardContent className="p-6 flex items-center gap-4">
                        <img src="/nanouk/energy_balanced.png" alt="Nanouk" className="w-16 h-16 object-contain" />
                        <p className="text-sm text-muted-foreground">
                            {de ? 'In dieser Woche gibt es keine Einträge. Logge fleissig, dann hat Nanouk nächsten Montag etwas zu erzählen!'
                                : 'No entries for this week. Keep logging and Nanouk will have a story next Monday!'}
                        </p>
                    </CardContent></Card>
                ) : (
                    <>
                        {/* Nanouk verdict */}
                        <Card className={`border ${grade.bg}`} data-testid="weekly-nanouk-card">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <img src={data.energy.week_delta <= 0 ? '/nanouk/energy_fit.png' : '/nanouk/energy_light.png'}
                                        alt="Nanouk" className="w-16 h-16 object-contain shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <GradeIcon className={`w-5 h-5 ${grade.color}`} />
                                            <span className="text-sm font-bold">{grade.label[de ? 'de' : 'en']}</span>
                                        </div>
                                        <p className="text-sm leading-relaxed" data-testid="weekly-nanouk-comment">
                                            {data.nanouk_comment}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Weekly streak medals */}
                        {offset === 0 && data.streak && data.streak.weeks > 0 && (
                            <Card className="border-amber-200" data-testid="weekly-streak-card">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <span className="text-3xl" data-testid="weekly-streak-medal">
                                        {data.streak.medal === 'gold' ? '🥇' : data.streak.medal === 'silver' ? '🥈' : data.streak.medal === 'bronze' ? '🥉' : '🔥'}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold" data-testid="weekly-streak-weeks">
                                            {de
                                                ? `${data.streak.weeks} ${data.streak.weeks === 1 ? 'Woche' : 'Wochen'} in Folge im Zielbereich!`
                                                : `${data.streak.weeks} ${data.streak.weeks === 1 ? 'week' : 'weeks'} in a row on target!`}
                                        </p>
                                        {data.streak.next_medal && (
                                            <p className="text-xs text-muted-foreground">
                                                {de
                                                    ? `Noch ${data.streak.next_medal.weeks_to_go} ${data.streak.next_medal.weeks_to_go === 1 ? 'Woche' : 'Wochen'} bis ${data.streak.next_medal.medal === 'gold' ? '🥇 Gold' : data.streak.next_medal.medal === 'silver' ? '🥈 Silber' : '🥉 Bronze'}`
                                                    : `${data.streak.next_medal.weeks_to_go} more ${data.streak.next_medal.weeks_to_go === 1 ? 'week' : 'weeks'} to ${data.streak.next_medal.medal === 'gold' ? '🥇 gold' : data.streak.next_medal.medal === 'silver' ? '🥈 silver' : '🥉 bronze'}`}
                                            </p>
                                        )}
                                        {!data.streak.next_medal && (
                                            <p className="text-xs text-muted-foreground">
                                                {de ? 'Höchste Medaille erreicht – Nanouk verneigt sich! 🐺' : 'Highest medal reached – Nanouk bows! 🐺'}
                                            </p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Taler week average */}
                        {data.taler.enabled && (
                            <Card className="border-amber-200 bg-amber-50/50" data-testid="weekly-taler-card">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Coins className="w-5 h-5 text-amber-600" />
                                            <span className="text-sm font-semibold">{de ? 'Taler-Wochenschnitt' : 'Weekly Taler average'}</span>
                                        </div>
                                        <span className="text-xl font-bold text-amber-700" data-testid="weekly-taler-avg">
                                            Ø {data.taler.avg_spent}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {de
                                            ? `Budget ${data.taler.budget} · Ø +${data.taler.avg_bonus} Sport-Bonus · Zielbereich ${data.taler.budget}–${data.taler.budget + 2}`
                                            : `Budget ${data.taler.budget} · avg +${data.taler.avg_bonus} sport bonus · target ${data.taler.budget}–${data.taler.budget + 2}`}
                                        {data.grade === 'gold' && ' 🏅'}
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <StatTile icon={Scale} iconClass={data.energy.week_delta > 0 ? 'text-red-600' : 'text-green-600'}
                                title={de ? 'Energie-Konto' : 'Energy account'}
                                value={<span className={data.energy.week_delta > 0 ? 'text-red-600' : 'text-green-600'}>{fmtSigned(data.energy.week_delta)} kcal</span>}
                                sub={data.energy.week_delta > 0
                                    ? (de ? `+${data.energy.fat_g} g aufs Polster 🧈` : `+${data.energy.fat_g} g to the reserve 🧈`)
                                    : (de ? `${Math.abs(data.energy.fat_g)} g Fett abgebaut 🔥` : `${Math.abs(data.energy.fat_g)} g fat burned 🔥`)}
                                testId="weekly-energy-tile" />
                            <StatTile icon={Apple} iconClass="text-green-600"
                                title={de ? 'Ernährung' : 'Nutrition'}
                                value={`Ø ${data.nutrition.avg_kcal.toLocaleString('de-CH')} kcal`}
                                sub={de ? `${data.nutrition.tracked_days} Tage getrackt · Ziel ${data.nutrition.calorie_goal.toLocaleString('de-CH')}` : `${data.nutrition.tracked_days} days tracked · goal ${data.nutrition.calorie_goal.toLocaleString('de-CH')}`}
                                testId="weekly-nutrition-tile" />
                            <StatTile icon={Dumbbell} iconClass="text-blue-600"
                                title={de ? 'Fitness' : 'Fitness'}
                                value={`${data.fitness.workouts} ${de ? 'Workouts' : 'workouts'}`}
                                sub={`${data.fitness.workout_kcal.toLocaleString('de-CH')} kcal · ${data.fitness.steps_total.toLocaleString('de-CH')} ${de ? 'Schritte' : 'steps'}`}
                                testId="weekly-fitness-tile" />
                            <StatTile icon={Moon} iconClass="text-indigo-500"
                                title={de ? 'Schlaf' : 'Sleep'}
                                value={data.sleep.nights ? `Ø ${data.sleep.avg_hours} h` : '—'}
                                sub={data.sleep.nights ? (de ? `${data.sleep.nights} Nächte erfasst` : `${data.sleep.nights} nights logged`) : (de ? 'Keine Schlafdaten' : 'No sleep data')}
                                testId="weekly-sleep-tile" />
                        </div>

                        {(data.nutrition.best_day || data.nutrition.worst_day) && (
                            <Card data-testid="weekly-best-worst-card">
                                <CardContent className="p-4 grid grid-cols-2 gap-3 text-sm">
                                    {data.nutrition.best_day && (
                                        <div>
                                            <p className="text-xs text-muted-foreground mb-0.5">{de ? 'Bester Tag' : 'Best day'} 🌟</p>
                                            <p className="font-semibold text-green-600">
                                                {fmtDate(data.nutrition.best_day.date, language)} · {fmtSigned(data.nutrition.best_day.balance)} kcal
                                            </p>
                                        </div>
                                    )}
                                    {data.nutrition.worst_day && data.nutrition.worst_day.date !== data.nutrition.best_day?.date && (
                                        <div>
                                            <p className="text-xs text-muted-foreground mb-0.5">{de ? 'Sünden-Tag' : 'Sin day'} 😅</p>
                                            <p className="font-semibold text-red-600">
                                                {fmtDate(data.nutrition.worst_day.date, language)} · {fmtSigned(data.nutrition.worst_day.balance)} kcal
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </motion.div>
        </div>
    );
}
